import React, { useState, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getPlanDetails } from "../utils/premiumHelpers";

export default function PaymentCheckout({ plan, onSuccess, onCancel, onError }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const razorpayScriptLoaded = useRef(false);

  const planDetails = getPlanDetails(plan);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        razorpayScriptLoaded.current = true;
        resolve(true);
      } else {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => {
          razorpayScriptLoaded.current = true;
          resolve(true);
        };
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      }
    });
  };

  // Initialize payment
  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load Razorpay. Please check your internet connection.");
      }

      // Create order via Cloud Function
      const functions = getFunctions();
      const createOrder = httpsCallable(functions, "createPremiumOrder");

      const orderResponse = await createOrder({ plan });
      const { orderId, key_id, amount } = orderResponse.data;

      // Razorpay options
      const options = {
        key: key_id,
        amount: amount * 100, // Convert to paise
        currency: "INR",
        name: "Indian Flatmates",
        description: `${planDetails.name} - Premium Subscription`,
        image: "https://indianflatmates.co.uk/logo_black.png",
        order_id: orderId,
        handler: (response) => handlePaymentSuccess(response, plan),
        prefill: {
          email: "",
          contact: "",
        },
        theme: {
          color: "#4F46E5",
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            onCancel?.();
          },
        },
      };

      // Open Razorpay checkout
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment initialization error:", err);
      const errorMsg = err.message || "Failed to initialize payment. Please try again.";
      setError(errorMsg);
      onError?.(errorMsg);
      setLoading(false);
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = async (response, selectedPlan) => {
    try {
      const { razorpay_payment_id, razorpay_order_id, razorpay_signature } = response;

      // Verify payment via Cloud Function
      const functions = getFunctions();
      const verifyPayment = httpsCallable(functions, "verifyPremiumPayment");

      const verifyResponse = await verifyPayment({
        paymentId: razorpay_payment_id,
        orderId: razorpay_order_id,
        signature: razorpay_signature,
      });

      setLoading(false);
      onSuccess?.(verifyResponse.data);
    } catch (err) {
      console.error("Payment verification error:", err);
      const errorMsg = err.message || "Payment verification failed. Please contact support.";
      setError(errorMsg);
      onError?.(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full shadow-2xl p-8">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Complete Payment</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Secure checkout with Razorpay</p>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600 dark:text-gray-400">Plan:</span>
            <span className="font-semibold text-gray-800 dark:text-white">{planDetails.name}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-600 dark:text-gray-400">Duration:</span>
            <span className="font-semibold text-gray-800 dark:text-white">
              {planDetails.durationMonths} month{planDetails.durationMonths > 1 ? "s" : ""}
            </span>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-600 my-3"></div>
          <div className="flex justify-between">
            <span className="font-bold text-gray-800 dark:text-white">Total:</span>
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
              ₹{planDetails.price.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Info Box */}
        <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-900 dark:text-blue-200">
            ✅ Secure payment powered by <strong>Razorpay</strong>
          </p>
          <p className="text-xs text-blue-800 dark:text-blue-300 mt-2">
            Your payment information is encrypted and secure.
          </p>
        </div>

        {/* Payment Methods Info */}
        <div className="mb-6 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Accepted Payment Methods:</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
            <div>💳 Credit/Debit Cards</div>
            <div>🏦 Netbanking</div>
            <div>📱 UPI</div>
            <div>💰 Wallets</div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={loading}
            className="flex-1 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Processing...
              </>
            ) : (
              <>🔒 Pay Now</>
            )}
          </button>
        </div>

        {/* Terms */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
          By proceeding, you agree to our Terms & Conditions
        </p>
      </div>
    </div>
  );
}
